import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, TrendingUp, Wallet, ArrowDownUp, Menu, X, LogOut, Settings, User } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const SWIPE_CLOSE_THRESHOLD = 90; // px

const MobileNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartYRef = useRef(null);
  const dragOffsetRef = useRef(0);
  const sheetRef = useRef(null);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { path: '/trading', icon: TrendingUp, label: 'Trade' },
    { path: '/swap', icon: ArrowDownUp, label: 'Swap' },
    { path: '/wallet', icon: Wallet, label: 'Wallet' },
  ];

  const closeMenu = () => {
    setShowMenu(false);
    setDragOffset(0);
    dragOffsetRef.current = 0;
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/signin');
    closeMenu();
  };

  // Escape key + Android back button (via history) to close
  useEffect(() => {
    if (!showMenu) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };

    // Push a history entry so hardware back gesture closes the sheet
    // without navigating away from the current page.
    window.history.pushState({ mobileMenuOpen: true }, '');
    const onPop = () => closeMenu();

    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);

    // Lock body scroll while sheet is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = prevOverflow;
      // If we still have our history entry, remove it silently
      if (window.history.state && window.history.state.mobileMenuOpen) {
        window.history.back();
      }
    };
  }, [showMenu]);

  // Swipe-down to dismiss
  const handleTouchStart = (e) => {
    touchStartYRef.current = e.touches[0].clientY;
    dragOffsetRef.current = 0;
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (touchStartYRef.current == null) return;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (dy > 0) {
      // only allow downward drag
      dragOffsetRef.current = dy;
      setDragOffset(dy);
    }
  };

  const handleTouchEnd = () => {
    // Read from ref to avoid stale-closure on very fast swipes
    if (dragOffsetRef.current > SWIPE_CLOSE_THRESHOLD) {
      closeMenu();
    } else {
      dragOffsetRef.current = 0;
      setDragOffset(0);
    }
    touchStartYRef.current = null;
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="mobile-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon className="h-5 w-5 mb-1" />
              <span>{item.label}</span>
            </Link>
          );
        })}

        {/* Menu Button */}
        <button
          onClick={() => setShowMenu(true)}
          className="mobile-nav-item focus:outline-none focus-visible:outline-none"
          aria-label="Open more menu"
          aria-haspopup="dialog"
          aria-expanded={showMenu}
          data-testid="mobile-nav-more-btn"
        >
          <Menu className="h-5 w-5 mb-1" />
          <span>More</span>
        </button>
      </nav>

      {/* Slide-up Menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-[200] bg-black/80"
          onClick={closeMenu}
          data-testid="mobile-menu-overlay"
          role="presentation"
        >
          <div
            ref={sheetRef}
            className="absolute bottom-0 left-0 right-0 bg-[#0a1628] rounded-t-3xl p-6"
            style={{
              paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))',
              transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
              transition: dragOffset ? 'none' : 'transform 200ms ease-out',
            }}
            onClick={e => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-menu-title"
            data-testid="mobile-menu-sheet"
          >
            {/* Drag handle */}
            <div
              className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-gray-600"
              aria-hidden="true"
              data-testid="mobile-menu-drag-handle"
            />

            <div className="flex items-center justify-between mb-6">
              <h3 id="mobile-menu-title" className="text-white font-bold text-xl">Menu</h3>
              <button
                onClick={closeMenu}
                className="p-2 hover:bg-gray-700 rounded-lg focus:outline-none"
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-2">
              <Link
                to="/profile"
                onClick={closeMenu}
                className="flex items-center space-x-3 p-4 rounded-xl bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
                data-testid="mobile-menu-profile-link"
              >
                <User className="h-5 w-5 text-purple-400" />
                <span className="text-white font-medium">Profile</span>
              </Link>

              <Link
                to="/settings"
                onClick={closeMenu}
                className="flex items-center space-x-3 p-4 rounded-xl bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
                data-testid="mobile-menu-settings-link"
              >
                <Settings className="h-5 w-5 text-purple-400" />
                <span className="text-white font-medium">Settings</span>
              </Link>

              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 p-4 rounded-xl bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 transition-colors focus:outline-none"
                data-testid="mobile-menu-logout-btn"
              >
                <LogOut className="h-5 w-5 text-red-400" />
                <span className="text-red-400 font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;
